import { useState } from 'react';
import { Link } from 'react-router-dom';
import type { CartItem } from '@/types';
import { formatCurrency } from '@/utils/helpers';

interface Props {
  item: CartItem;
  onUpdateQty: (productId: string, qty: number, variantId?: string) => void;
  onRemove: (productId: string, variantId?: string) => void;
}

const FALLBACK_IMG =
  'https://readdy.ai/api/search-image?query=solar%20panel%20renewable%20energy%20product%20clean%20white%20background%20professional%20ecommerce%20photography%20minimal&width=200&height=200&seq=cart-item-fallback&orientation=squarish';

export default function CartDrawerItem({ item, onUpdateQty, onRemove }: Props) {
  const { product, quantity, variantId } = item;
  const subtotal = product.price * quantity;
  const maxQty = (product.stockQty != null && product.stockQty > 0) ? product.stockQty : 999;
  const [imgErr, setImgErr] = useState(false);
  const [removing, setRemoving] = useState(false);

  const handleRemove = () => {
    setRemoving(true);
    setTimeout(() => onRemove(product.id, variantId), 280);
  };

  const discount = product.originalPrice && product.originalPrice > product.price
    ? Math.round(((product.originalPrice - product.price) / product.originalPrice) * 100)
    : 0;

  return (
    <div
      className="flex gap-3 p-3 rounded-2xl transition-all duration-280"
      style={{
        background: 'rgba(255,255,255,0.85)',
        border: '1px solid rgba(0,0,0,0.06)',
        opacity: removing ? 0 : 1,
        transform: removing ? 'translateX(20px) scale(0.97)' : 'translateX(0) scale(1)',
      }}
      role="listitem"
    >
      {/* Image — bigger, tappable */}
      <Link
        to={`/products/${product.id}`}
        className="flex-shrink-0 rounded-xl overflow-hidden relative cursor-pointer active:scale-95 transition-transform"
        style={{ width: '76px', height: '76px', background: '#f5f0ea' }}
        aria-label={`Ver ${product.name}`}
      >
        <img
          src={imgErr ? FALLBACK_IMG : (product.thumbnail || FALLBACK_IMG)}
          alt={product.name}
          className="w-full h-full object-contain p-1.5"
          onError={() => setImgErr(true)}
        />
        {discount > 0 && (
          <span className="absolute top-1 left-1 text-[9px] font-black px-1.5 py-0.5 rounded-md bg-rose-500 text-white leading-none">
            -{discount}%
          </span>
        )}
      </Link>

      {/* Info */}
      <div className="flex-1 min-w-0 flex flex-col justify-between">
        {/* Top row: name + remove */}
        <div className="flex items-start justify-between gap-1.5">
          <div className="min-w-0">
            <p className="text-[9px] font-bold uppercase tracking-widest text-emerald-500 mb-0.5 truncate">
              {product.categoryId.replace(/-/g, ' ')}
            </p>
            <h4 className="text-[12px] font-semibold text-stone-800 leading-snug line-clamp-2">
              {product.name}
            </h4>
          </div>
          <button
            onClick={handleRemove}
            className="w-6 h-6 flex items-center justify-center rounded-lg text-stone-400 hover:text-red-500 hover:bg-red-50 transition-colors cursor-pointer flex-shrink-0 mt-0.5"
            aria-label={`Eliminar ${product.name}`}
          >
            <i className="ri-close-line text-sm" />
          </button>
        </div>

        {/* Bottom row: stepper + price */}
        <div className="flex items-center justify-between mt-2">
          {/* Qty stepper — big touch targets */}
          <div
            className="flex items-center rounded-xl overflow-hidden"
            style={{ background: '#ede8e0', height: '34px' }}
          >
            <button
              onClick={() => onUpdateQty(product.id, quantity - 1, variantId)}
              disabled={quantity <= 1}
              className="w-9 h-full flex items-center justify-center text-stone-500 hover:text-stone-900 active:bg-stone-200 disabled:opacity-25 disabled:cursor-not-allowed transition-colors cursor-pointer"
              aria-label="Reducir cantidad"
            >
              <i className="ri-subtract-line text-sm" />
            </button>
            <span className="w-8 text-center text-[13px] font-black text-stone-800 select-none">
              {quantity}
            </span>
            <button
              onClick={() => onUpdateQty(product.id, quantity + 1, variantId)}
              disabled={quantity >= maxQty}
              className="w-9 h-full flex items-center justify-center text-stone-500 hover:text-stone-900 active:bg-stone-200 disabled:opacity-25 disabled:cursor-not-allowed transition-colors cursor-pointer"
              aria-label="Aumentar cantidad"
            >
              <i className="ri-add-line text-sm" />
            </button>
          </div>

          {/* Price */}
          <div className="text-right">
            <p className="text-[15px] font-black text-stone-900 leading-none">
              {formatCurrency(subtotal)}
            </p>
            {quantity > 1 && (
              <p className="text-[10px] text-stone-400 mt-0.5">
                {formatCurrency(product.price)} c/u
              </p>
            )}
          </div>
        </div>
      </div>
    </div>
  );
}
